import * as yup from 'yup'

import { ACCEPTED_IMAGE_TYPES, ACCEPTED_VIDEO_TYPES } from 'constants/AcceptedImageAndVideoFiles'
import { ALLOWED_CHARS, URL_CHARS } from 'constants/fieldsValidationValues'

export const makeCreativeStep1Schema = (meta: string, robloxCreativeType: number) =>
  yup.object().shape({
    robloxUrl: yup
      .string()
      .test('linkRequired', 'Creative link is required', (value) => {
        if (meta === 'roblox') return !!value?.length
        return true
      })
      .test('is roblox URL', 'Invalid link', (value) => {
        if (meta === 'roblox')
          return (
            RegExp('^http:\\/\\/www\\.roblox\\.com\\/asset\\/\\?id=[0-9]+').test(value || '') ||
            RegExp('^rbxassetid:\\/\\/[0-9]+').test(value || '')
          )
        return true
      }),
    robloxLength: yup
      .string()
      .test('lengthRequired', 'Video Creative length is required', (value) => {
        if (meta === 'roblox' && robloxCreativeType === 11) return !!value?.length
        return true
      })
      .test('isNumber', 'Video length should be a number greater than 1', (value) => {
        if (meta === 'roblox' && robloxCreativeType === 11)
          return !!value?.match(/^([1-9][0-9]*\.?[0-9]*)$/gi) && value !== '0'
        return true
      }),
    files: yup
      .mixed()
      .test('fileRequired', `Please add an image ${meta === 'roblox' ? '' : 'or video'} file`, (value) => {
        return !!value?.length
      })
      .test('fileType', `The file should be of image ${meta === 'roblox' ? '' : 'or video'} type`, (value) => {
        if (!value?.length) return true // attachment is optional
        if (meta === 'roblox') return value[0]?.type.includes('image')
        return value[0]?.type.includes('image') || value[0]?.type.includes('video')
      })
      .test('imageSize', 'Maximum image size is 2MB', (value) => {
        if (!value?.length) return true // attachment is optional
        if (value[0]?.type.includes('image')) return value[0].size <= 2 * 2 ** 20
        return true
      })
      .test('videoSize', 'Maximum video size is 15MB', (value) => {
        if (!value?.length) return true // attachment is optional
        if (meta === 'roblox') return true
        if (value[0]?.type.includes('video')) return value[0].size <= 15 * 2 ** 20
        return true
      })
      .test('fileSizeMin', 'Minimum file size is 1 byte', (value) => {
        if (!value.length) return true // attachment is optional
        return value[0].size > 0
      })
      .test('imageFormat', 'The image should be of .jpg, .jpeg or .png type', (value) => {
        if (!value?.length) return true // attachment is optional
        if (value[0]?.type.includes('image')) return ACCEPTED_IMAGE_TYPES.includes(value[0]?.type?.split('/').pop())
        return true
      })
      .test('videoFormat', 'The video should be of .mp4 or .webm type', (value) => {
        if (!value?.length) return true // attachment is optional
        if (meta === 'roblox') return true
        if (value[0]?.type.includes('video')) return ACCEPTED_VIDEO_TYPES.includes(value[0]?.type?.split('/').pop())
        return true
      }),
  })

export const makeCreativeStep2Schema = (creativeType: number) =>
  yup.object().shape({
    name: yup
      .string()
      .trim()
      .typeError('Name should be a string')
      .required('Name is a required field')
      .matches(ALLOWED_CHARS, `Name can only contain latin letters`)
      .min(3, 'Name should contain at least 3 symbols')
      .max(60, 'Name should contain up to 60 symbols'),
    description: yup
      .string()
      .trim()
      .required('Description is a required field')
      .min(3, 'Description should contain at least 3 symbols')
      .max(255, 'Description should contain up to 255 symbols')
      .matches(ALLOWED_CHARS, `Description can only contain latin letters`),
    externalLink: yup
      .string()
      .test(
        'link-validation',
        `Invalid ${creativeType === 10 || creativeType === 11 ? 'teleport ID' : 'link'}`,
        (value) => {
          if (!value?.length) return true // is optional
          if (creativeType === 10 || creativeType === 11) return /^\d+$/.test(value)
          return URL_CHARS.test(value)
        }
      )
      .max(255, 'Link should contain up to 255 symbols'),
  })

export const makeStreamStep1Schema = (streamType: string) =>
  yup.object().shape({
    streamUrl: yup
      .string()
      .test('streamRequired', 'Stream url is required', (value) => {
        if (streamType === 'metaads' && !value?.length) return true
        else return !!value?.length
      })
      .test('m3u8-validation', 'Should contain `.m3u8`', (value) => {
        if (streamType === 'M3U' && value?.length) return value.includes('.m3u8')
        else return true
      })
      .test('yt-validation', 'Should be YouTube URL', (value) => {
        if (streamType === 'YouTube' && value?.length) return value.includes('youtube') || value.includes('youtu.be')
        else return true
      })
      .test('twitch-validation', 'Should be Twitch URL', (value) => {
        if (streamType === 'Twitch' && value?.length) return value.includes('twitch')
        else return true
      }),
    files: yup
      .mixed()
      .test('fileRequired', 'Please add an image file', (value) => {
        return !!value?.length
      })
      .test('imageSize', 'Maximum image size is 2MB', (value) => {
        if (!value?.length) return true // attachment is optional
        return value[0].size <= 2 * 2 ** 20
      })
      .test('fileSizeMin', 'Minimum image size is 1 byte', (value) => {
        if (!value.length) return true // attachment is optional
        return value[0].size > 0
      })
      .test('imageFormat', 'The preview image should be of .jpg, .jpeg or .png type', (value) => {
        if (!value?.length) return true // attachment is optional
        return ACCEPTED_IMAGE_TYPES.includes(value[0]?.type?.split('/').pop())
      }),
  })
